import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { gsap } from "gsap";
import { SITE_BASE_URL } from "../config/api";

const RefundPolicy = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    // GSAP Animations
    gsap.fromTo(
      containerRef.current,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" }
    );
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Refund Policy - Knots & Krafts</title>
        <meta
          name="description"
          content="Read Knots & Krafts' refund policy for hand-knitted and crochet products. Learn about our order cancellation, return, and refund procedures."
        />
        <meta
          name="keywords"
          content="Knots & Krafts, Refund Policy, Return Policy, Cancellation Policy, Hand-Knitted Products, Crochet Products, Shipping Policy"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href={`${SITE_BASE_URL.replace(/\/+$/, "")}/refund-policy`}
        />
      </Helmet>

      {/* Header Section */}
      <header className="relative h-64 sm:h-80 md:h-96 flex flex-col justify-center items-center text-center">
        {/* Background Image */}
        <img
          src="./shop_hero.jpg"
          alt="Refund Policy - Knots & Krafts"
          className="absolute inset-0 w-full h-full object-cover opacity-100"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        {/* Content */}
        <div className="relative z-10 text-white px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold">
            Refund Policy
          </h1>
          <nav className="mt-4 text-sm sm:text-base">
            <a href="/" className="hover:underline">
              Home
            </a>{" "}
            &gt; <span>Refund Policy</span>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <div
        ref={containerRef}
        className="max-w-4xl mx-auto py-12 px-6 sm:px-8"
        itemScope
        itemType="https://schema.org/WebPage"
      >
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
          <header className="mb-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Refund Policy
            </h2>
          </header>

          <main className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              At <strong>Knots & Krafts</strong>, we are committed to providing
              you with high-quality hand-knitted and crochet products. Please
              read our refund policy carefully to understand your rights and our
              obligations.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-6">
              Order Cancellation & Modification Policy
            </h2>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Non-Cancellation of Orders
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Once an order is placed, it cannot be cancelled. We initiate the
              packing process promptly to ensure timely dispatch and delivery of
              your products.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Order Modifications
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              If you wish to make any modifications, such as changing quantities
              or adjusting the order value, please contact us before 8 AM on the
              next working day following your order placement. Note that no
              refunds will be issued for modifications; ensure the total order
              value remains equivalent or higher.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Processing Time
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Orders are sent to our warehouse for packing at 8 AM on the next
              working day after your order is placed.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Delivery Timeframe
            </h3>
            <ul className="list-disc ml-6 text-gray-700 leading-relaxed mb-6 space-y-2">
              <li>
                <strong>Domestic Shipping:</strong> Delivery within India
                typically takes 2-14 days, depending on your proximity to
                Bengaluru.
              </li>
              <li>
                <strong>International Shipping:</strong> Delivery outside India
                can take 2-4 weeks, depending on your location.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Order Accuracy
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Please review all order details carefully, including product
              specifications, quantities, and shipping addresses, before
              confirming your order.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-12 mb-6">
              Return Policy
            </h2>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Knitting and Crochet Supplies & Related Products
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              All yarns, threads, needles, hooks, accessories, and items related
              to knitting, crochet, and sewing are{" "}
              <strong>non-refundable</strong>,<strong> non-exchangeable</strong>
              , and <strong>non-returnable</strong>. If the packaging is damaged
              or tampered with upon delivery, please refuse to accept the
              shipment and notify us immediately by sending images via WhatsApp
              at{" "}
              <a
                href="tel:+919820293979"
                className="text-primary-600 hover:underline"
              >
                +91 98202 93979
              </a>
              .
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Hand-Knitted & Crocheted Products
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              If you are not completely satisfied with our hand-knitted or
              crocheted products, you may return them within{" "}
              <strong>7 days</strong> of delivery for a refund. The items must
              be in their original, unused, and unworn condition with all tags
              attached.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Return Instructions
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              To initiate a return, please contact our customer support at{" "}
              <a
                href="mailto:knots.krafts@gmail.com"
                className="text-primary-600 hover:underline"
              >
                knots.krafts@gmail.com
              </a>{" "}
              with your order details and reason for return.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Damaged or Defective Products
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              If you receive a defective or damaged product, please provide an
              unpacking video as proof and contact us within 7 days of delivery.
              We will review your claim and, if valid, issue a refund to your
              original payment method.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-12 mb-6">
              Refund Policy
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Once we receive and inspect your returned item, we will notify you
              of the approval or rejection of your refund. Approved refunds will
              be processed, and a credit will automatically be applied to your
              original payment method within a certain number of days.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Non-Refundable Situations
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Refunds will not be issued in the following cases:
            </p>
            <ul className="list-disc ml-6 text-gray-700 leading-relaxed mb-6 space-y-2">
              <li>Incorrect or incomplete shipping address provided.</li>
              <li>Consignee unavailable or uncontactable during delivery.</li>
              <li>Shipment refused by the consignee.</li>
              <li>
                Items not in their original condition, damaged, or missing parts
                for reasons not due to our error.
              </li>
              <li>Items returned after 7 days of delivery.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Shipping Returns
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              To return your product, please mail it to:
              <br />
              <strong className="text-gray-800">
                Knots & Krafts,
                <br />
                302, JSJ Opal Apartments,
                <br />
                BHEL Officers Layout, Jayanagar,
                <br />
                Bengaluru, Karnataka 560029, India
              </strong>
              .
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              You will be responsible for paying your own shipping costs for
              returning your item. Shipping costs are non-refundable.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              Need Help?
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              For any questions regarding our refund policy, please contact us
              at{" "}
              <a
                href="mailto:knots.krafts@gmail.com"
                className="text-primary-600 hover:underline"
              >
                knots.krafts@gmail.com
              </a>{" "}
              or call us at{" "}
              <a
                href="tel:+919480424751"
                className="text-primary-600 hover:underline"
              >
                +91 94804 24751
              </a>
              .
            </p>
          </main>

          {/* Call to Action */}
          <div className="text-center bg-gray-50 rounded-xl p-8 mt-12">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Ready to Start Shopping?
            </h3>
            <p className="text-gray-600 mb-6">
              Explore our wide range of crafting supplies and materials
            </p>
            <a
              href="/shop"
              className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Shop Now
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
